import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, message, Empty, Space } from 'antd';
import { ArrowRightOutlined, CrownOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { superadminUsersApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { FormSkeleton } from '../../components/SkeletonLoaders';

const { Option } = Select;

const pickSuperAdmin = (res) => {
  const d = res?.data || res;
  return d.superAdmin || d.super_admin || null;
};

const SuperadminUserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/superadmin-users', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await superadminUsersApi.getEditMeta(id);
        const d = res?.data || res;
        if (cancelled) return;
        const u = pickSuperAdmin(res);
        if (!u) {
          setNotFound(true);
          setUser(null);
          return;
        }
        setUser(u);
        setRoles(d.roles || []);
        const roleId = u.roles?.[0]?.id ?? u.roles?.[0]?.role_id ?? u.role_id;
        form.setFieldsValue({
          name: u.name,
          email: u.email,
          role: roleId,
          status: u.status === 'deactive' ? 'deactive' : 'active',
        });
      } catch (error) {
        if (!cancelled) {
          message.error(error?.message || 'خطا در دریافت اطلاعات');
          setNotFound(true);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const onFinish = async (values) => {
    const payload = {
      name: values.name?.trim(),
      email: values.email?.trim(),
      role: values.role,
      status: values.status,
    };
    setSubmitting(true);
    try {
      await superadminUsersApi.update(id, payload);
      message.success('ذخیره شد');
      goBackToList();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
          <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                  <CrownOutlined className="text-xl text-violet-600 dark:text-violet-400" />
                </div>
                <div className="min-w-0">
                  <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ویرایش سوپر ادمین</h1>
                  <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">در حال بارگذاری…</p>
                </div>
              </div>
            </div>
            <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
              <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <FormSkeleton fields={5} />
              </Card>
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (notFound) {
    return (
      <MainLayout>
        <div className="custom-font p-6" style={{ fontFamily: 'CustomFont' }}>
          <Empty description="کاربر یافت نشد" />
          <Button className="mt-4" onClick={goBackToList}>
            بازگشت
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                <CrownOutlined className="text-xl text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ویرایش سوپر ادمین</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">نام، ایمیل، نقش و وضعیت</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {user?.image_url ? (
                <div className="mb-6 min-w-0 rounded-xl border border-gray-100 bg-white/80 p-3 dark:border-gray-700 dark:bg-gray-900/30">
                  <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">تصویر فعلی</div>
                  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                    <img src={user.image_url} alt="" className="mx-auto block max-h-40 object-contain" loading="lazy" />
                  </div>
                </div>
              ) : null}
              <Form form={form} layout="vertical" onFinish={onFinish}>
                <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                  <Form.Item name="name" label="نام" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="email" label="ایمیل" rules={[{ required: true, type: 'email' }]}>
                    <Input dir="ltr" />
                  </Form.Item>
                  <Form.Item name="role" label="نقش" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font" placeholder="نقش">
                      {roles.map((r) => (
                        <Option key={r.id} value={r.id}>
                          {r.display_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font">
                      <Option value="active">فعال</Option>
                      <Option value="deactive">غیرفعال</Option>
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item className="mb-0">
                  <Space wrap>
                    <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                      ذخیره تغییرات
                    </Button>
                    <Button onClick={goBackToList}>انصراف</Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminUserEdit;
